import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import {
  Scale, RefreshCw, Settings, Download, Loader2, Sparkles, FileText,
  Trash2, Eye, XCircle, RotateCcw, Search, Paperclip, Power, Wifi, Save,
  BarChart3, History, ListChecks, Plus, Edit2,
  CheckCircle2, ChevronUp, ChevronDown, ExternalLink
} from 'lucide-react';
import DialogEscritoIAAbogados from './DialogEscritoIAAbogados';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/automatizacion/abogados`;
const AUTOM = `${process.env.REACT_APP_BACKEND_URL}/api/automatizacion`;
const APIROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('lexduo_token')}` });

const ESTADOS = [
  { value: 'descargado', label: 'Sin procesar' },
  { value: 'pendiente_revision', label: 'Para revisar' },
  { value: 'confirmado', label: 'Confirmados' },
  { value: 'error', label: 'Errores' },
  { value: 'eliminado_origen', label: 'Borrados en Gmail' },
  { value: 'descartado', label: 'Descartados' },
  { value: '', label: 'Todos' },
];

const badgeFor = (estado) => {
  const map = {
    descargado: { cls: 'bg-gray-100 text-gray-700', txt: 'Sin procesar' },
    procesando_ia: { cls: 'bg-blue-100 text-blue-700', txt: 'Analizando…' },
    pendiente_revision: { cls: 'bg-amber-100 text-amber-800', txt: 'Para revisar' },
    confirmado: { cls: 'bg-green-100 text-green-700', txt: 'Confirmado' },
    descartado: { cls: 'bg-gray-100 text-gray-400', txt: 'Descartado' },
    eliminado_origen: { cls: 'bg-rose-100 text-rose-700', txt: 'Borrado en Gmail' },
    error: { cls: 'bg-red-100 text-red-700', txt: 'Error' },
  };
  return map[estado] || { cls: 'bg-gray-100 text-gray-600', txt: estado };
};

const EmailsAbogados = () => {
  const [config, setConfig] = useState(null);
  const [configForm, setConfigForm] = useState({
    nombre: 'Abogados', imap_host: 'imap.gmail.com', imap_port: 993,
    imap_user: '', imap_password: '', imap_use_ssl: true, imap_carpeta: 'INBOX',
    auto_fetch: false, auto_fetch_intervalo_min: 15, auto_process_ia: true,
  });
  const [configDialog, setConfigDialog] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingConn, setTestingConn] = useState(false);

  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterEstado, setFilterEstado] = useState('descargado');
  const [search, setSearch] = useState('');

  const [fetching, setFetching] = useState(false);
  const [fetchDialog, setFetchDialog] = useState(false);
  const [fetchForm, setFetchForm] = useState({ fecha_desde: '', fecha_hasta: '', remitente: '', limit: 50 });

  const [detailDialog, setDetailDialog] = useState({ open: false, email: null });
  const [detailLoading, setDetailLoading] = useState(false);
  const [processingIA, setProcessingIA] = useState(null);
  const [savingProposal, setSavingProposal] = useState(false);
  const [expSearch, setExpSearch] = useState('');
  const [expResults, setExpResults] = useState([]);

  const [escritoDialog, setEscritoDialog] = useState({ open: false, email: null });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [batchRunning, setBatchRunning] = useState(false);
  const [subTab, setSubTab] = useState('emails');
  const [selectedExp, setSelectedExp] = useState(null);
  const [confirmForm, setConfirmForm] = useState({ titulo: '', tipo_tramite: '', resumen: '' });
  const [savingReg, setSavingReg] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [rightTab, setRightTab] = useState('adjuntos');
  const [actuacionesPrevias, setActuacionesPrevias] = useState([]);
  const [loadingActuaciones, setLoadingActuaciones] = useState(false);
  const [expandedActId, setExpandedActId] = useState(null);
  const [adjuntosPorAct, setAdjuntosPorAct] = useState({});
  const [actPreview, setActPreview] = useState({ id: null, url: '', nombre: '' });
  const [emailPreview, setEmailPreview] = useState({ idx: null, url: '', nombre: '' });

  const loadConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${BASE}/config`, { headers: authHeaders() });
      if (r.data.config) {
        setConfig(r.data.config);
        setConfigForm(f => ({ ...f, ...r.data.config, imap_password: '' }));
      } else {
        setConfig(null);
      }
    } catch (e) { /* silencio */ }
  }, []);

  const loadEmails = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filterEstado) params.estado = filterEstado;
      const r = await axios.get(`${BASE}/emails`, { headers: authHeaders(), params });
      setEmails(r.data.emails || []);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudieron cargar los emails', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [filterEstado]);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { loadEmails(); }, [loadEmails]);
  useEffect(() => { setSelectedIds(new Set()); }, [filterEstado]);

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.post(`${BASE}/config`, configForm, { headers: authHeaders() });
      toast({ title: 'Configuración guardada' });
      setConfigDialog(false);
      loadConfig();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo guardar', variant: 'destructive' });
    } finally { setSavingConfig(false); }
  };

  const testConnection = async () => {
    setTestingConn(true);
    try {
      const r = await axios.post(`${BASE}/config/test-connection`, configForm, { headers: authHeaders() });
      toast({ title: r.data.success ? 'Conexión OK' : 'Sin conexión', description: r.data.message,
        variant: r.data.success ? 'default' : 'destructive' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Fallo de conexión', variant: 'destructive' });
    } finally { setTestingConn(false); }
  };

  const toggleActive = async () => {
    try {
      const r = await axios.post(`${BASE}/config/toggle-active`, {}, { headers: authHeaders() });
      toast({ title: r.data.message });
      loadConfig();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo cambiar el estado', variant: 'destructive' });
    }
  };

  const doFetch = async () => {
    setFetching(true);
    try {
      const payload = {
        fecha_desde: fetchForm.fecha_desde || null,
        fecha_hasta: fetchForm.fecha_hasta || null,
        remitente: fetchForm.remitente || null,
        limit: Number(fetchForm.limit) || 50,
      };
      const r = await axios.post(`${BASE}/emails/fetch`, payload, { headers: authHeaders() });
      toast({ title: 'Descarga completada', description: r.data.message });
      setFetchDialog(false);
      loadEmails();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudieron descargar los emails', variant: 'destructive' });
    } finally { setFetching(false); }
  };

  const openDetail = async (id) => {
    setDetailDialog({ open: true, email: null });
    setDetailLoading(true);
    setExpSearch(''); setExpResults([]);
    try {
      const r = await axios.get(`${BASE}/emails/${id}`, { headers: authHeaders() });
      const e = r.data;
      setDetailDialog({ open: true, email: e });
      setConfirmForm({
        titulo: (e.titulo_confirmado || e.ia_titulo || '').toUpperCase(),
        tipo_tramite: e.tipo_tramite_confirmado || e.ia_tipo_tramite || 'tramite',
        resumen: e.resumen_confirmado || e.ia_resumen || '',
      });
      if (e.expediente_id_confirmado || e.ia_expediente_id) {
        setSelectedExp({ id: e.expediente_id_confirmado || e.ia_expediente_id, numero_expediente: e.ia_expediente_numero || '', titulo: '' });
      } else { setSelectedExp(null); }
      setEmailPreview({ idx: null, url: '', nombre: '' });
      setActPreview({ id: null, url: '', nombre: '' });
      setRightTab('adjuntos');
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo abrir el email', variant: 'destructive' });
      setDetailDialog({ open: false, email: null });
    } finally { setDetailLoading(false); }
  };

  const processIA = async (email) => {
    setProcessingIA(email.id);
    try {
      const r = await axios.post(`${BASE}/emails/${email.id}/process-ia`, {}, { headers: authHeaders() });
      toast({ title: 'Análisis IA completado', description: r.data.ia_titulo || 'Propuesta generada' });
      if (detailDialog.open && detailDialog.email?.id === email.id) {
        await openDetail(email.id);
      }
      loadEmails();
    } catch (e) {
      toast({ title: 'Error IA', description: e.response?.data?.detail || 'No se pudo analizar', variant: 'destructive' });
    } finally { setProcessingIA(null); }
  };

  const updateDetailField = (field, value) => {
    setDetailDialog(d => ({ ...d, email: { ...d.email, [field]: value } }));
  };

  const searchExp = async (q) => {
    setExpSearch(q);
    if (!q || q.length < 2) { setExpResults([]); return; }
    try {
      const r = await axios.get(`${AUTOM}/expedientes/buscar`, { headers: authHeaders(), params: { q } });
      setExpResults(r.data.expedientes || r.data || []);
    } catch (e) { setExpResults([]); }
  };

  const selectExp = (exp) => {
    setSelectedExp({ id: exp.id, numero_expediente: exp.numero_expediente, titulo: exp.titulo });
    setExpResults([]); setExpSearch('');
  };

  useEffect(() => {
    if (!selectedExp?.id) { setActuacionesPrevias([]); return; }
    setLoadingActuaciones(true);
    setExpandedActId(null); setActPreview({ id: null, url: '', nombre: '' });
    axios.get(`${APIROOT}/expedientes/${selectedExp.id}/actuaciones`, { headers: authHeaders() })
      .then(r => setActuacionesPrevias(Array.isArray(r.data) ? r.data : (r.data.actuaciones || [])))
      .catch(() => setActuacionesPrevias([]))
      .finally(() => setLoadingActuaciones(false));
  }, [selectedExp]);

  const toggleExpandAct = async (id) => {
    if (expandedActId === id) { setExpandedActId(null); return; }
    setExpandedActId(id); setActPreview({ id: null, url: '', nombre: '' });
    if (!adjuntosPorAct[id]) {
      try {
        const r = await axios.get(`${APIROOT}/adjuntos/actuacion/${id}`, { headers: authHeaders() });
        setAdjuntosPorAct(prev => ({ ...prev, [id]: Array.isArray(r.data) ? r.data : [] }));
      } catch (e) { setAdjuntosPorAct(prev => ({ ...prev, [id]: [] })); }
    }
  };

  const previewActAdjunto = async (adj) => {
    if (actPreview.id === adj.id) { setActPreview({ id: null, url: '', nombre: '' }); return; }
    try {
      const r = await axios.get(`${APIROOT}/adjuntos/download/${adj.id}`, { headers: authHeaders(), responseType: 'blob' });
      setActPreview({ id: adj.id, url: window.URL.createObjectURL(r.data), nombre: adj.nombre });
    } catch (e) { toast({ title: 'No se pudo previsualizar', variant: 'destructive' }); }
  };

  const previewEmailAdjunto = async (idx, nombre) => {
    if (emailPreview.idx === idx) { setEmailPreview({ idx: null, url: '', nombre: '' }); return; }
    try {
      const r = await axios.get(`${BASE}/emails/${detailDialog.email.id}/preview-adjunto/${idx}`, { headers: authHeaders(), responseType: 'blob' });
      setEmailPreview({ idx, url: window.URL.createObjectURL(r.data), nombre });
    } catch (e) { toast({ title: 'No se pudo previsualizar', variant: 'destructive' }); }
  };

  const handleGuardar = async () => {
    const emc = detailDialog.email;
    if (!emc) return;
    setSavingReg(true);
    try {
      await axios.put(`${BASE}/emails/${emc.id}`, {
        expediente_id_confirmado: selectedExp?.id ? Number(selectedExp.id) : null,
        ia_titulo: confirmForm.titulo,
        ia_resumen: confirmForm.resumen,
        ia_tipo_tramite: confirmForm.tipo_tramite || 'tramite',
      }, { headers: authHeaders() });
      toast({ title: 'Registro guardado' });
      loadEmails();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo guardar', variant: 'destructive' });
    } finally { setSavingReg(false); }
  };

  const saveProposal = async () => {
    const em = detailDialog.email;
    if (!em) return;
    setSavingProposal(true);
    try {
      await axios.put(`${BASE}/emails/${em.id}`, {
        expediente_id_confirmado: em.expediente_id_confirmado || null,
        ia_titulo: em.ia_titulo || null,
        ia_resumen: em.ia_resumen || null,
        ia_tipo_tramite: em.ia_tipo_tramite || null,
      }, { headers: authHeaders() });
      toast({ title: 'Propuesta guardada' });
      loadEmails();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo guardar', variant: 'destructive' });
    } finally { setSavingProposal(false); }
  };

  const discardEmail = async (id) => {
    try {
      await axios.post(`${BASE}/emails/${id}/discard`, {}, { headers: authHeaders() });
      toast({ title: 'Email descartado' });
      loadEmails();
      if (detailDialog.email?.id === id) setDetailDialog({ open: false, email: null });
    } catch (e) { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const restoreEmail = async (id) => {
    try {
      await axios.post(`${BASE}/emails/${id}/restore`, {}, { headers: authHeaders() });
      toast({ title: 'Email restaurado' });
      loadEmails();
    } catch (e) { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const deleteEmail = async (id) => {
    try {
      await axios.delete(`${BASE}/emails/${id}`, { headers: authHeaders() });
      toast({ title: 'Email eliminado' });
      setDeleteConfirm(null);
      loadEmails();
      if (detailDialog.email?.id === id) setDetailDialog({ open: false, email: null });
    } catch (e) { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const filtered = emails.filter(e => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (e.asunto || '').toLowerCase().includes(q) ||
           (e.remitente || '').toLowerCase().includes(q) ||
           (e.remitente_nombre || '').toLowerCase().includes(q) ||
           (e.ia_titulo || '').toLowerCase().includes(q);
  });

  const em = detailDialog.email;

  const toggleSelect = (id) => {
    setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const toggleSelectAll = () => {
    setSelectedIds(prev => (prev.size === filtered.length && filtered.length > 0) ? new Set() : new Set(filtered.map(e => e.id)));
  };
  const runBatch = async (accion) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBatchRunning(true);
    let ok = 0, err = 0;
    for (const id of ids) {
      try {
        if (accion === 'ia') await axios.post(`${BASE}/emails/${id}/process-ia`, {}, { headers: authHeaders() });
        else if (accion === 'discard') await axios.post(`${BASE}/emails/${id}/discard`, {}, { headers: authHeaders() });
        else if (accion === 'delete') await axios.delete(`${BASE}/emails/${id}`, { headers: authHeaders() });
        ok++;
      } catch (e) { err++; }
    }
    setBatchRunning(false);
    setSelectedIds(new Set());
    toast({ title: 'Acción por lotes completada', description: `${ok} correcto(s)${err ? `, ${err} con error` : ''}` });
    loadEmails();
  };

  return (
    <div className="space-y-4" data-testid="emails-abogados">
      {/* Sub-pestañas */}
      <div className="flex items-center gap-1 border-b border-gray-200" data-testid="abg-subtabs">
        {[['emails', 'Emails'], ['historial', 'Historial'], ['reglas', 'Reglas'], ['estadisticas', 'Estadísticas']].map(([v, l]) => (
          <button key={v} onClick={() => setSubTab(v)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-all ${subTab === v ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid={`abg-subtab-${v}`}>{l}</button>
        ))}
      </div>

      {subTab === 'emails' && (<>
      {/* Header / acciones */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Scale className="h-5 w-5 text-indigo-600" />
          <div>
            <h2 className="text-base font-semibold text-gray-900">Correos de Abogados</h2>
            <p className="text-xs text-gray-500">
              {config ? `${config.imap_user || 'sin cuenta'} · ${config.activo ? 'activa' : 'pausada'}` : 'Sin configurar'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {config && (
            <Button variant="outline" size="sm" onClick={toggleActive} data-testid="abg-toggle-active">
              <Power className={`h-4 w-4 mr-1.5 ${config.activo ? 'text-green-600' : 'text-gray-400'}`} />
              {config.activo ? 'Activa' : 'Pausada'}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setConfigDialog(true)} data-testid="abg-open-config">
            <Settings className="h-4 w-4 mr-1.5" /> Configuración
          </Button>
          <Button size="sm" onClick={() => setFetchDialog(true)} disabled={!config?.activo}
            className="bg-indigo-600 hover:bg-indigo-700" data-testid="abg-open-fetch">
            <Download className="h-4 w-4 mr-1.5" /> Descargar correos
          </Button>
          <Button variant="outline" size="sm" onClick={loadEmails} data-testid="abg-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {!config && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800" data-testid="abg-no-config">
          No hay ninguna cuenta configurada. Pulsa <strong>Configuración</strong> para conectar el buzón de abogados
          (por ejemplo <code>advocat1lexduo@gmail.com</code> o una cuenta propia como <code>abogados@lexduo.cat</code>).
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        {ESTADOS.map(s => (
          <button key={s.value}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${filterEstado === s.value ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            onClick={() => setFilterEstado(s.value)}
            data-testid={`abg-filter-${s.value || 'todos'}`}>
            {s.label}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="h-4 w-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar…"
            className="pl-8 h-9 w-48" data-testid="abg-search" />
        </div>
      </div>

      {/* Barra de acciones por lotes */}
      {filtered.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="abg-batch-bar">
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0}
              onChange={toggleSelectAll} data-testid="abg-select-all" />
            Seleccionar todo
          </label>
          {selectedIds.size > 0 && (
            <div className="flex flex-wrap items-center gap-2 ml-2">
              <span className="text-gray-500">{selectedIds.size} seleccionado(s)</span>
              <Button size="sm" variant="outline" disabled={batchRunning} onClick={() => runBatch('ia')} data-testid="abg-batch-ia">
                {batchRunning ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5 text-amber-500" />} Analizar IA
              </Button>
              <Button size="sm" variant="outline" disabled={batchRunning} onClick={() => runBatch('discard')} data-testid="abg-batch-discard">
                <XCircle className="h-4 w-4 mr-1.5" /> Descartar
              </Button>
              <Button size="sm" variant="outline" disabled={batchRunning} onClick={() => runBatch('delete')} className="text-red-600" data-testid="abg-batch-delete">
                <Trash2 className="h-4 w-4 mr-1.5" /> Eliminar
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Listado */}
      {loading ? (
        <div className="py-16 text-center text-gray-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-gray-400 text-sm" data-testid="abg-empty">No hay correos.</div>
      ) : (
        <div className="space-y-2" data-testid="abg-email-list">
          {filtered.map(e => {
            const b = badgeFor(e.estado);
            return (
              <Card key={e.id} className="hover:shadow-sm transition-shadow" data-testid={`abg-email-${e.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-3">
                    <input type="checkbox" className="mt-1.5 shrink-0" checked={selectedIds.has(e.id)}
                      onChange={() => toggleSelect(e.id)} onClick={ev => ev.stopPropagation()} data-testid={`abg-check-${e.id}`} />
                    <div className="min-w-0 flex-1 cursor-pointer" onClick={() => openDetail(e.id)}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={b.cls} data-testid={`abg-estado-${e.id}`}>{b.txt}</Badge>
                        {e.tiene_adjuntos && <Paperclip className="h-3.5 w-3.5 text-gray-400" />}
                        {e.es_urgente && <Badge className="bg-red-100 text-red-700">Urgente</Badge>}
                        {e.ia_expediente_numero && <span className="text-xs text-indigo-600 font-medium">Exp. {e.ia_expediente_numero}</span>}
                      </div>
                      <p className="font-medium text-sm text-gray-900 truncate mt-1">{e.asunto || '(sin asunto)'}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {e.remitente_nombre || e.remitente} · {e.fecha_email ? new Date(e.fecha_email).toLocaleString('es-ES') : ''}
                      </p>
                      {e.ia_titulo && <p className="text-xs text-gray-600 mt-1 truncate">IA: {e.ia_titulo}</p>}
                      {e.estado === 'error' && e.error_mensaje && <p className="text-xs text-red-600 mt-1 truncate" data-testid={`abg-row-error-${e.id}`}>Error: {e.error_mensaje}</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => openDetail(e.id)} title="Ver" data-testid={`abg-ver-${e.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {e.estado !== 'confirmado' && e.estado !== 'descartado' && (
                        <Button variant="ghost" size="sm" onClick={() => processIA(e)} disabled={processingIA === e.id}
                          title="Analizar IA" data-testid={`abg-ia-${e.id}`}>
                          {processingIA === e.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-amber-500" />}
                        </Button>
                      )}
                      {e.estado !== 'descartado' && (
                        <Button variant="ghost" size="sm" onClick={() => setEscritoDialog({ open: true, email: e })}
                          title="Generar escrito IA" data-testid={`abg-escrito-${e.id}`}>
                          <FileText className="h-4 w-4 text-indigo-600" />
                        </Button>
                      )}
                      {e.estado === 'descartado' ? (
                        <Button variant="ghost" size="sm" onClick={() => restoreEmail(e.id)} title="Restaurar" data-testid={`abg-restore-${e.id}`}>
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => discardEmail(e.id)} title="Descartar" data-testid={`abg-discard-${e.id}`}>
                          <XCircle className="h-4 w-4 text-gray-400" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(e.id)} title="Eliminar" data-testid={`abg-delete-${e.id}`}>
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      </>)}

      {subTab === 'historial' && <HistorialAbogados />}
      {subTab === 'reglas' && <ReglasAbogados />}
      {subTab === 'estadisticas' && <EstadisticasAbogados />}

      {/* Dialog: configuración IMAP */}
      <Dialog open={configDialog} onOpenChange={setConfigDialog}>
        <DialogContent className="max-w-lg" data-testid="abg-config-dialog">
          <DialogHeader><DialogTitle>Configuración de la cuenta (Abogados)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nombre descriptivo</Label>
              <Input value={configForm.nombre} onChange={e => setConfigForm({ ...configForm, nombre: e.target.value })} data-testid="abg-cfg-nombre" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Servidor IMAP</Label>
                <Input value={configForm.imap_host} onChange={e => setConfigForm({ ...configForm, imap_host: e.target.value })} data-testid="abg-cfg-host" />
              </div>
              <div>
                <Label>Puerto</Label>
                <Input type="number" value={configForm.imap_port} onChange={e => setConfigForm({ ...configForm, imap_port: Number(e.target.value) })} data-testid="abg-cfg-port" />
              </div>
            </div>
            <div>
              <Label>Usuario (email)</Label>
              <Input value={configForm.imap_user} onChange={e => setConfigForm({ ...configForm, imap_user: e.target.value })}
                placeholder="advocat1lexduo@gmail.com" data-testid="abg-cfg-user" />
            </div>
            <div>
              <Label>Contraseña de aplicación {config && <span className="text-xs text-gray-400">(déjalo vacío para no cambiarla)</span>}</Label>
              <Input type="password" value={configForm.imap_password} onChange={e => setConfigForm({ ...configForm, imap_password: e.target.value })} data-testid="abg-cfg-pass" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Carpeta</Label>
                <Input value={configForm.imap_carpeta} onChange={e => setConfigForm({ ...configForm, imap_carpeta: e.target.value })} data-testid="abg-cfg-folder" />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={configForm.imap_use_ssl} onCheckedChange={v => setConfigForm({ ...configForm, imap_use_ssl: v })} data-testid="abg-cfg-ssl" />
                <Label className="cursor-pointer">Usar SSL</Label>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={configForm.auto_process_ia} onCheckedChange={v => setConfigForm({ ...configForm, auto_process_ia: v })} data-testid="abg-cfg-autoia" />
              <Label className="cursor-pointer">Analizar con IA automáticamente al descargar</Label>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={testConnection} disabled={testingConn} data-testid="abg-cfg-test">
              {testingConn ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wifi className="h-4 w-4 mr-2" />} Probar conexión
            </Button>
            <Button onClick={saveConfig} disabled={savingConfig} className="bg-indigo-600 hover:bg-indigo-700" data-testid="abg-cfg-save">
              {savingConfig ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: descargar correos */}
      <Dialog open={fetchDialog} onOpenChange={setFetchDialog}>
        <DialogContent className="max-w-md" data-testid="abg-fetch-dialog">
          <DialogHeader><DialogTitle>Descargar correos</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Desde</Label>
                <Input type="date" value={fetchForm.fecha_desde} onChange={e => setFetchForm({ ...fetchForm, fecha_desde: e.target.value })} data-testid="abg-fetch-desde" />
              </div>
              <div>
                <Label>Hasta</Label>
                <Input type="date" value={fetchForm.fecha_hasta} onChange={e => setFetchForm({ ...fetchForm, fecha_hasta: e.target.value })} data-testid="abg-fetch-hasta" />
              </div>
            </div>
            <div>
              <Label>Remitente (opcional)</Label>
              <Input value={fetchForm.remitente} onChange={e => setFetchForm({ ...fetchForm, remitente: e.target.value })} placeholder="email@ejemplo.com" data-testid="abg-fetch-remitente" />
            </div>
            <div>
              <Label>Máximo de correos</Label>
              <Input type="number" value={fetchForm.limit} onChange={e => setFetchForm({ ...fetchForm, limit: e.target.value })} data-testid="abg-fetch-limit" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setFetchDialog(false)}>Cancelar</Button>
            <Button onClick={doFetch} disabled={fetching} className="bg-indigo-600 hover:bg-indigo-700" data-testid="abg-fetch-run">
              {fetching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Descargar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: LECTURA (sin procesar) / CONFIRMAR Y CREAR ACTUACIÓN (analizado) */}
      <Dialog open={detailDialog.open} onOpenChange={(v) => !v && setDetailDialog({ open: false, email: null })}>
        <DialogContent className="overflow-y-auto p-4 sm:p-6" style={{ maxWidth: '98vw', width: '98vw', maxHeight: '98vh', height: '98vh' }} data-testid="abg-detail-dialog">
          {(() => { const analizado = !!(em && (em.ia_titulo || em.ia_procesado_at)); return (
          <>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              {analizado
                ? <><FileText className="h-5 w-5 text-green-600" /> Confirmar y generar escrito IA</>
                : <><Eye className="h-5 w-5 text-indigo-600" /> Leer email sin procesar</>}
              {analizado && em.tiene_plazo && <Badge className="bg-orange-100 text-orange-700 ml-2">Con plazo</Badge>}
            </DialogTitle>
          </DialogHeader>
          {detailLoading || !em ? (
            <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /></div>
          ) : (
            <div className="space-y-4">
              {/* Cabecera info email */}
              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded border">
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="font-medium text-gray-800">{em.asunto || '(sin asunto)'}</span>
                  <span className="text-xs text-gray-400">|</span>
                  <span className="text-xs">{em.remitente_nombre || em.remitente}</span>
                  <span className="text-xs text-gray-400">|</span>
                  <span className="text-xs">{em.fecha_email ? new Date(em.fecha_email).toLocaleString('es-ES') : ''}</span>
                  {em.tiene_adjuntos && (<><span className="text-xs text-gray-400">|</span>
                    <span className="text-xs"><Paperclip className="h-3 w-3 inline mr-1" />{(em.adjuntos || []).length} adjunto(s)</span></>)}
                </div>
              </div>
              {em.error_mensaje && <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700">{em.error_mensaje}</div>}

              {!analizado ? (
                /* ===================== VISTA LECTURA (email sin procesar) ===================== */
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="abg-vista-lectura">
                  <div className="space-y-2">
                    <Label className="font-semibold flex items-center gap-1.5"><Eye className="h-4 w-4" /> Texto del email</Label>
                    <div className="bg-white border rounded p-4 text-sm text-gray-800 whitespace-pre-wrap overflow-y-auto" style={{ maxHeight: '72vh' }} data-testid="abg-lectura-body">
                      {em.cuerpo_texto || '(sin texto)'}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-semibold flex items-center gap-1.5"><Paperclip className="h-4 w-4" /> Documentos adjuntos</Label>
                    {(em.adjuntos || []).length === 0 ? (
                      <p className="text-sm text-gray-400 py-6 text-center">Este email no tiene adjuntos.</p>
                    ) : (
                      <>
                        <div className="flex flex-wrap gap-2">
                          {(em.adjuntos || []).map((a, i) => (
                            <Button key={i} variant={emailPreview.idx === i ? 'default' : 'outline'} size="sm"
                              className={`h-8 text-xs ${emailPreview.idx === i ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-white'}`}
                              onClick={() => previewEmailAdjunto(i, a.nombre)} data-testid={`abg-preview-adj-${i}`}>
                              <FileText className="h-3.5 w-3.5 mr-1.5" /> {a.nombre}
                            </Button>
                          ))}
                        </div>
                        {emailPreview.url ? (
                          <div className="border rounded-lg bg-gray-100 mt-2" data-testid="abg-email-pdf-viewer">
                            <div className="px-2.5 py-1.5 bg-gray-200 border-b text-[11px] text-gray-700 flex items-center justify-between">
                              <span className="truncate"><FileText className="h-3 w-3 inline mr-1 text-red-600" />{emailPreview.nombre}</span>
                              <button onClick={() => window.open(emailPreview.url, '_blank')} title="Abrir en pestaña nueva"><ExternalLink className="h-3.5 w-3.5" /></button>
                            </div>
                            <iframe src={emailPreview.url} title={emailPreview.nombre} className="w-full bg-white" style={{ height: '66vh', border: 'none' }} />
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 mt-1">Pulsa un adjunto para previsualizarlo.</p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ) : (
                /* ===================== VISTA CONFIRMAR (email analizado por IA) ===================== */
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="abg-vista-confirmar">
                  {/* Columna izquierda: formulario + IA + texto email */}
                  <div className="space-y-4">
                    <div>
                      <Label className="font-semibold">Expediente *</Label>
                      {selectedExp ? (
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <a href={`/expedientes/${selectedExp.id}`} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-100 text-indigo-800 hover:bg-indigo-200 hover:underline border border-indigo-300 transition-colors"
                            data-testid="abg-confirm-exp-link">
                            <span className="text-2xl font-bold tracking-wide">{selectedExp.numero_expediente || `#${selectedExp.id}`}</span>
                          </a>
                          {selectedExp.titulo && <span className="text-sm text-gray-600">{selectedExp.titulo}</span>}
                          <Button variant="ghost" size="sm" onClick={() => setSelectedExp(null)} title="Quitar"><XCircle className="h-4 w-4 text-gray-400" /></Button>
                        </div>
                      ) : (
                        <div className="mt-1 relative">
                          <Input placeholder="Buscar expediente por número o título…" value={expSearch}
                            onChange={e => searchExp(e.target.value)} data-testid="abg-confirm-exp-search" />
                          {expResults.length > 0 && (
                            <div className="absolute z-20 bg-white border rounded shadow mt-1 w-full max-h-48 overflow-y-auto">
                              {expResults.map(x => (
                                <button key={x.id} className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                  onClick={() => selectExp(x)} data-testid={`abg-exp-opt-${x.id}`}>
                                  <span className="font-medium text-indigo-700">{x.numero_expediente}</span> — {x.titulo}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <Label className="font-semibold">Título de la actuación *</Label>
                      <Textarea rows={2} value={confirmForm.titulo} onChange={e => setConfirmForm({ ...confirmForm, titulo: e.target.value })}
                        className="uppercase font-medium resize-none" data-testid="abg-confirm-titulo" />
                    </div>
                    <div>
                      <Label className="font-semibold">Tipo de trámite</Label>
                      <Input value={confirmForm.tipo_tramite} onChange={e => setConfirmForm({ ...confirmForm, tipo_tramite: e.target.value })}
                        placeholder="ej: contestacion_demanda" data-testid="abg-confirm-tipo" />
                    </div>
                    <div>
                      <Label className="font-semibold">Resumen (IA)</Label>
                      <Textarea rows={4} value={confirmForm.resumen} onChange={e => setConfirmForm({ ...confirmForm, resumen: e.target.value })} data-testid="abg-confirm-resumen" />
                    </div>

                    {em.tiene_plazo && (
                      <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded p-3 text-sm text-amber-800" data-testid="abg-aviso-plazo">
                        <span className="text-lg leading-none">⚠️</span>
                        <div>
                          <span className="font-semibold">Atención: se indica un plazo para presentar escrito</span>
                          {(em.aviso_plazo || em.fecha_limite) && (
                            <span>{': '}{em.aviso_plazo || ''}{em.fecha_limite ? ` (fecha límite: ${new Date(em.fecha_limite).toLocaleDateString('es-ES')})` : ''}</span>
                          )}
                        </div>
                      </div>
                    )}

                    <details className="border rounded" data-testid="abg-confirm-body-toggle">
                      <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-gray-600 flex items-center gap-1.5"><Eye className="h-4 w-4" /> Ver texto del email</summary>
                      <div className="px-3 pb-3 text-sm text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto">{em.cuerpo_texto || '(sin texto)'}</div>
                    </details>
                  </div>

                  {/* Columna derecha: pestañas Adjuntos / Actuaciones */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-1 border-b">
                      <button onClick={() => setRightTab('adjuntos')} data-testid="abg-right-tab-adjuntos"
                        className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${rightTab === 'adjuntos' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500'}`}>
                        <Paperclip className="h-4 w-4 inline mr-1" /> Adjuntos a incorporar
                      </button>
                      <button onClick={() => setRightTab('actuaciones')} disabled={!selectedExp} data-testid="abg-right-tab-actuaciones"
                        className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${rightTab === 'actuaciones' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500'} ${!selectedExp ? 'opacity-40' : ''}`}>
                        Actuaciones del expediente {actuacionesPrevias.length > 0 && <Badge variant="outline" className="ml-1 text-[10px]">{actuacionesPrevias.length}</Badge>}
                      </button>
                    </div>

                    {rightTab === 'adjuntos' && (
                      <div className="space-y-2 mt-2" data-testid="abg-adjuntos-tab">
                        {(em.adjuntos || []).length === 0 ? (
                          <p className="text-sm text-gray-400 py-6 text-center">Este email no tiene adjuntos.</p>
                        ) : (
                          <>
                            <div className="text-sm text-gray-600 flex items-center gap-1.5">
                              <Paperclip className="h-4 w-4" /> Se adjuntarán <strong>{(em.adjuntos || []).length}</strong> archivo(s) a la actuación
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {(em.adjuntos || []).map((a, i) => (
                                <Button key={i} variant={emailPreview.idx === i ? 'default' : 'outline'} size="sm"
                                  className={`h-8 text-xs ${emailPreview.idx === i ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-white'}`}
                                  onClick={() => previewEmailAdjunto(i, a.nombre)} data-testid={`abg-preview-adj-${i}`}>
                                  <FileText className="h-3.5 w-3.5 mr-1.5" /> {a.nombre}
                                </Button>
                              ))}
                            </div>
                            {emailPreview.url && (
                              <div className="border rounded-lg bg-gray-100 mt-2" data-testid="abg-email-pdf-viewer">
                                <div className="px-2.5 py-1.5 bg-gray-200 border-b text-[11px] text-gray-700 flex items-center justify-between">
                                  <span className="truncate"><FileText className="h-3 w-3 inline mr-1 text-red-600" />{emailPreview.nombre}</span>
                                  <button onClick={() => window.open(emailPreview.url, '_blank')} title="Abrir en pestaña nueva"><ExternalLink className="h-3.5 w-3.5" /></button>
                                </div>
                                <iframe src={emailPreview.url} title={emailPreview.nombre} className="w-full bg-white" style={{ height: '58vh', border: 'none' }} />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {rightTab === 'actuaciones' && (
                      <div className="mt-2" data-testid="abg-actuaciones-tab">
                        {!selectedExp ? (
                          <p className="text-sm text-gray-400 py-6 text-center">Selecciona un expediente.</p>
                        ) : loadingActuaciones ? (
                          <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
                        ) : actuacionesPrevias.length === 0 ? (
                          <p className="text-sm text-gray-400 py-6 text-center">Este expediente no tiene actuaciones previas.</p>
                        ) : (
                          <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="abg-actuaciones-list">
                            {actuacionesPrevias.map(act => {
                              const isExp = expandedActId === act.id;
                              const adjs = adjuntosPorAct[act.id] || [];
                              return (
                                <Card key={act.id} className={isExp ? 'border-blue-300' : 'hover:bg-gray-50'} data-testid={`abg-act-${act.id}`}>
                                  <button type="button" onClick={() => toggleExpandAct(act.id)} className="w-full text-left p-3 flex items-start gap-2">
                                    {isExp ? <ChevronUp className="h-4 w-4 text-blue-600 mt-0.5" /> : <ChevronDown className="h-4 w-4 text-gray-400 mt-0.5" />}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <Badge variant="outline" className="text-[10px] uppercase">{act.tipo || 'tramite'}</Badge>
                                        <span className="text-xs text-gray-500">{act.fecha ? new Date(act.fecha).toLocaleDateString('es-ES') : ''}</span>
                                      </div>
                                      <p className="text-sm font-medium text-gray-800 mt-1 truncate">{act.titulo || '(Sin título)'}</p>
                                    </div>
                                  </button>
                                  {isExp && (
                                    <div className="border-t bg-gray-50/50 px-3 py-3 space-y-3">
                                      {act.descripcion && <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans">{act.descripcion}</pre>}
                                      <div>
                                        <p className="text-[11px] font-semibold text-gray-500 uppercase mb-1.5">Adjuntos {adjs.length > 0 && `(${adjs.length})`}</p>
                                        {adjs.length === 0 ? <p className="text-xs text-gray-400 italic">Sin adjuntos.</p> : (
                                          <div className="flex flex-wrap gap-1.5">
                                            {adjs.map(adj => (
                                              <Button key={adj.id} variant={actPreview.id === adj.id ? 'default' : 'outline'} size="sm"
                                                className={`h-7 text-[11px] ${actPreview.id === adj.id ? 'bg-blue-600 text-white' : 'bg-white'}`}
                                                onClick={() => previewActAdjunto(adj)} data-testid={`abg-act-adj-${adj.id}`}>
                                                <FileText className="h-3 w-3 mr-1" /> {adj.nombre}
                                              </Button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      {actPreview.id && adjs.some(a => a.id === actPreview.id) && (
                                        <div className="border rounded-lg bg-white">
                                          <div className="px-2.5 py-1.5 bg-gray-100 border-b text-[11px] flex items-center justify-between">
                                            <span className="truncate"><FileText className="h-3 w-3 inline mr-1 text-red-600" />{actPreview.nombre}</span>
                                            <button onClick={() => window.open(actPreview.url, '_blank')}><ExternalLink className="h-3 w-3" /></button>
                                          </div>
                                          <iframe src={actPreview.url} title={actPreview.nombre} className="w-full bg-white" style={{ height: '40vh', border: 'none' }} />
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </Card>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex flex-wrap gap-2 border-t pt-4">
            {em && !analizado && (
              <>
                <Button variant="outline" onClick={() => setDetailDialog({ open: false, email: null })} data-testid="abg-lectura-cerrar">Cerrar</Button>
                <Button variant="outline" onClick={() => discardEmail(em.id)} className="text-gray-600" data-testid="abg-lectura-descartar">
                  <XCircle className="h-4 w-4 mr-2" /> Descartar
                </Button>
                <Button className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => processIA(em)} disabled={processingIA === em.id} data-testid="abg-lectura-analizar">
                  {processingIA === em.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Analizar con IA
                </Button>
              </>
            )}
            {em && analizado && (
              <>
                <Button variant="outline" onClick={() => processIA(em)} disabled={processingIA === em.id} className="mr-auto text-purple-600" data-testid="abg-confirm-reanalizar">
                  {processingIA === em.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1.5" />} Volver a analizar
                </Button>
                <Button variant="outline" onClick={() => setDetailDialog({ open: false, email: null })} data-testid="abg-confirm-cancelar">Cancelar</Button>
                <Button variant="outline" onClick={handleGuardar} disabled={savingReg} data-testid="abg-confirm-guardar">
                  {savingReg ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Guardar
                </Button>
                <Button className="bg-green-600 hover:bg-green-700" onClick={() => setEscritoDialog({ open: true, email: em })} data-testid="abg-detail-generar-escrito">
                  <FileText className="h-4 w-4 mr-2" /> Generar escrito IA
                </Button>
              </>
            )}
          </DialogFooter>
          </>
          ); })()}
        </DialogContent>
      </Dialog>

      {/* Confirmar eliminación */}
      <Dialog open={!!deleteConfirm} onOpenChange={(v) => !v && setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="abg-delete-dialog">
          <DialogHeader><DialogTitle>Eliminar email</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">Esta acción no se puede deshacer. ¿Eliminar el email permanentemente?</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={() => deleteEmail(deleteConfirm)} data-testid="abg-delete-confirm">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escrito IA */}
      <DialogEscritoIAAbogados
        open={escritoDialog.open}
        email={escritoDialog.email}
        onClose={() => setEscritoDialog({ open: false, email: null })}
        onSaved={() => { setEscritoDialog({ open: false, email: null }); loadEmails(); }}
      />
    </div>
  );
};

// ===========================================================================
// Panel: HISTORIAL (confirmados / descartados)
// ===========================================================================
const HistorialAbogados = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    axios.get(`${BASE}/emails`, { headers: authHeaders(), params: { limit: 300 } })
      .then(r => setRows((r.data.emails || []).filter(e => ['confirmado', 'descartado'].includes(e.estado))))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /></div>;
  if (!rows.length) return <div className="py-16 text-center text-gray-400 text-sm" data-testid="abg-historial-empty">Aún no hay correos confirmados ni descartados.</div>;
  return (
    <div className="overflow-x-auto" data-testid="abg-historial">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b">
            <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Asunto</th>
            <th className="py-2 pr-3">Remitente</th><th className="py-2 pr-3">Estado</th>
            <th className="py-2 pr-3">Expediente</th><th className="py-2 pr-3">Confirmado por</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(e => (
            <tr key={e.id} className="border-b hover:bg-gray-50" data-testid={`abg-hist-row-${e.id}`}>
              <td className="py-2 pr-3 whitespace-nowrap text-gray-500">{e.fecha_email ? new Date(e.fecha_email).toLocaleDateString('es-ES') : ''}</td>
              <td className="py-2 pr-3 max-w-xs truncate">{e.asunto}</td>
              <td className="py-2 pr-3 truncate">{e.remitente_nombre || e.remitente}</td>
              <td className="py-2 pr-3">
                <Badge className={e.estado === 'confirmado' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}>
                  {e.estado === 'confirmado' ? 'Confirmado' : 'Descartado'}
                </Badge>
              </td>
              <td className="py-2 pr-3">{e.ia_expediente_numero || '—'}</td>
              <td className="py-2 pr-3 text-gray-500">
                {e.confirmado_por_nombre || '—'}
                {e.fecha_confirmacion && <span className="block text-xs">{new Date(e.fecha_confirmacion).toLocaleString('es-ES')}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ===========================================================================
// Panel: REGLAS de automatización
// ===========================================================================
const EMPTY_REGLA = { nombre: '', activa: true, prioridad: 10, condicion_remitente: '',
  condicion_asunto_contiene: '', condicion_cuerpo_contiene: '', accion_tipo_tramite: '', accion_titulo_prefijo: '' };

const ReglasAbogados = () => {
  const [reglas, setReglas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, regla: null });
  const [form, setForm] = useState(EMPTY_REGLA);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${BASE}/reglas`, { headers: authHeaders() })
      .then(r => setReglas(r.data.reglas || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY_REGLA); setDialog({ open: true, regla: null }); };
  const openEdit = (r) => { setForm({ ...EMPTY_REGLA, ...r }); setDialog({ open: true, regla: r }); };

  const save = async () => {
    if (!form.nombre.trim()) { toast({ title: 'Falta el nombre', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const payload = { ...form, prioridad: Number(form.prioridad) || 10 };
      if (dialog.regla) await axios.put(`${BASE}/reglas/${dialog.regla.id}`, payload, { headers: authHeaders() });
      else await axios.post(`${BASE}/reglas`, payload, { headers: authHeaders() });
      toast({ title: dialog.regla ? 'Regla actualizada' : 'Regla creada' });
      setDialog({ open: false, regla: null }); load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo guardar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await axios.delete(`${BASE}/reglas/${id}`, { headers: authHeaders() }); toast({ title: 'Regla eliminada' }); load(); }
    catch (e) { toast({ title: 'Error', variant: 'destructive' }); }
  };

  return (
    <div className="space-y-4" data-testid="abg-reglas">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Las reglas activas se aplican al analizar con IA (sobrescriben la propuesta si coinciden), por orden de prioridad.</p>
        <Button size="sm" onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700" data-testid="abg-regla-nueva">
          <Plus className="h-4 w-4 mr-1.5" /> Nueva regla
        </Button>
      </div>
      {loading ? (
        <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /></div>
      ) : reglas.length === 0 ? (
        <div className="py-12 text-center text-gray-400 text-sm" data-testid="abg-reglas-empty">No hay reglas configuradas.</div>
      ) : (
        <div className="space-y-2">
          {reglas.map(r => (
            <Card key={r.id} data-testid={`abg-regla-${r.id}`}>
              <CardContent className="p-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge className={r.activa ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}>{r.activa ? 'Activa' : 'Inactiva'}</Badge>
                    <span className="font-medium text-sm">{r.nombre}</span>
                    <span className="text-xs text-gray-400">prioridad {r.prioridad}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {r.condicion_remitente && `De: ${r.condicion_remitente}  `}
                    {r.condicion_asunto_contiene && `Asunto~ "${r.condicion_asunto_contiene}"  `}
                    {r.condicion_cuerpo_contiene && `Cuerpo~ "${r.condicion_cuerpo_contiene}"`}
                    {!r.condicion_remitente && !r.condicion_asunto_contiene && !r.condicion_cuerpo_contiene && 'Sin condiciones'}
                  </p>
                  <p className="text-xs text-indigo-600 mt-0.5">
                    {r.accion_tipo_tramite && `→ tipo: ${r.accion_tipo_tramite}  `}
                    {r.accion_titulo_prefijo && `→ prefijo: "${r.accion_titulo_prefijo}"`}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`abg-regla-edit-${r.id}`}><Edit2 className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(r.id)} data-testid={`abg-regla-del-${r.id}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialog.open} onOpenChange={(v) => !v && setDialog({ open: false, regla: null })}>
        <DialogContent className="max-w-lg" data-testid="abg-regla-dialog">
          <DialogHeader><DialogTitle>{dialog.regla ? 'Editar regla' : 'Nueva regla'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Nombre</Label>
                <Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} data-testid="abg-regla-nombre" />
              </div>
              <div>
                <Label>Prioridad</Label>
                <Input type="number" value={form.prioridad} onChange={e => setForm({ ...form, prioridad: e.target.value })} data-testid="abg-regla-prioridad" />
              </div>
            </div>
            <p className="text-xs font-medium text-gray-500 pt-1">Condiciones (se combinan con Y)</p>
            <Input placeholder="Remitente contiene…" value={form.condicion_remitente || ''} onChange={e => setForm({ ...form, condicion_remitente: e.target.value })} data-testid="abg-regla-cond-remitente" />
            <Input placeholder="Asunto contiene…" value={form.condicion_asunto_contiene || ''} onChange={e => setForm({ ...form, condicion_asunto_contiene: e.target.value })} data-testid="abg-regla-cond-asunto" />
            <Input placeholder="Cuerpo contiene…" value={form.condicion_cuerpo_contiene || ''} onChange={e => setForm({ ...form, condicion_cuerpo_contiene: e.target.value })} data-testid="abg-regla-cond-cuerpo" />
            <p className="text-xs font-medium text-gray-500 pt-1">Acciones</p>
            <Input placeholder="Tipo de trámite (ej: recurso_apelacion)" value={form.accion_tipo_tramite || ''} onChange={e => setForm({ ...form, accion_tipo_tramite: e.target.value })} data-testid="abg-regla-accion-tipo" />
            <Input placeholder="Prefijo del título (ej: [URGENTE])" value={form.accion_titulo_prefijo || ''} onChange={e => setForm({ ...form, accion_titulo_prefijo: e.target.value })} data-testid="abg-regla-accion-prefijo" />
            <div className="flex items-center gap-2">
              <Switch checked={form.activa} onCheckedChange={v => setForm({ ...form, activa: v })} data-testid="abg-regla-activa" />
              <Label className="cursor-pointer">Regla activa</Label>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialog({ open: false, regla: null })}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="abg-regla-guardar">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===========================================================================
// Panel: ESTADÍSTICAS (workload + gráficos + export)
// ===========================================================================
const CHART_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

const EstadisticasAbogados = () => {
  const [kpis, setKpis] = useState(null);
  const [estados, setEstados] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [remitentes, setRemitentes] = useState([]);
  const [serie, setSerie] = useState([]);
  const [periodo, setPeriodo] = useState('dia');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${BASE}/estadisticas/kpis`, { headers: authHeaders() }),
      axios.get(`${BASE}/estadisticas/por-estado`, { headers: authHeaders() }),
      axios.get(`${BASE}/estadisticas/por-tipo-tramite`, { headers: authHeaders() }),
      axios.get(`${BASE}/estadisticas/por-usuario`, { headers: authHeaders() }),
      axios.get(`${BASE}/estadisticas/por-remitente`, { headers: authHeaders() }),
    ]).then(([k, es, ti, us, re]) => {
      setKpis(k.data); setEstados(es.data.estados || []); setTipos(ti.data.tipos || []);
      setUsuarios(us.data.usuarios || []); setRemitentes(re.data.remitentes || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    axios.get(`${BASE}/estadisticas/volumen-temporal`, { headers: authHeaders(), params: { periodo } })
      .then(r => setSerie(r.data.serie || [])).catch(() => {});
  }, [periodo]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${BASE}/estadisticas/export`, { headers: authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `estadisticas_abogados_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo exportar', variant: 'destructive' });
    } finally { setExporting(false); }
  };

  if (loading) return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /></div>;

  const cards = kpis ? [
    { label: 'Recibidos', value: kpis.total_emails, cls: 'text-gray-900' },
    { label: 'Analizados IA', value: `${kpis.analizados_ia} (${kpis.tasa_analisis}%)`, cls: 'text-blue-600' },
    { label: 'Actuaciones creadas', value: kpis.actuaciones_creadas, cls: 'text-green-600' },
    { label: 'Pendientes', value: kpis.pendientes, cls: 'text-amber-600' },
    { label: 'Urgentes', value: kpis.urgentes_pendientes, cls: 'text-red-600' },
    { label: 'Con plazo', value: kpis.con_plazo, cls: 'text-purple-600' },
    { label: 'Errores', value: kpis.errores, cls: 'text-red-500' },
    { label: 'Tiempo medio (min)', value: kpis.tiempo_medio_minutos, cls: 'text-indigo-600' },
  ] : [];

  return (
    <div className="space-y-6" data-testid="abg-estadisticas">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-gray-500">Carga de trabajo real del buzón de abogados.</p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={loadAll} data-testid="abg-stats-refresh"><RefreshCw className="h-4 w-4" /></Button>
          <Button size="sm" onClick={exportCsv} disabled={exporting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="abg-stats-export">
            {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Exportar CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {cards.map(c => (
          <Card key={c.label}><CardContent className="p-3">
            <p className="text-xs text-gray-500">{c.label}</p>
            <p className={`text-xl font-bold ${c.cls}`} data-testid={`abg-kpi-${c.label}`}>{c.value}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Volumen temporal */}
      <Card><CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold flex items-center gap-1.5"><BarChart3 className="h-4 w-4 text-indigo-600" /> Volumen y procesamiento por {periodo === 'dia' ? 'día' : periodo}</h3>
          <div className="flex gap-1">
            {[['dia', 'Día'], ['semana', 'Semana'], ['mes', 'Mes']].map(([v, l]) => (
              <button key={v} onClick={() => setPeriodo(v)}
                className={`px-2.5 py-1 rounded text-xs ${periodo === v ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                data-testid={`abg-periodo-${v}`}>{l}</button>
            ))}
          </div>
        </div>
        {serie.length === 0 ? <p className="text-sm text-gray-400">Sin datos.</p> : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={serie} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="periodo" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="recibidos" name="Recibidos" stroke="#6366f1" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="analizados" name="Analizados IA" stroke="#3b82f6" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="confirmados" name="Confirmados" stroke="#10b981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent></Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Por estado (pie) */}
        <Card><CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-2">Distribución por estado</h3>
          {estados.length === 0 ? <p className="text-sm text-gray-400">Sin datos.</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={estados} dataKey="total" nameKey="label" cx="50%" cy="50%" outerRadius={85} label={(e) => `${e.label}: ${e.total}`}>
                  {estados.map((e, i) => <Cell key={i} fill={e.color || CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </CardContent></Card>

        {/* Por tipo de tramite (tareas) */}
        <Card><CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-2">Tareas por tipo de trámite</h3>
          {tipos.length === 0 ? <p className="text-sm text-gray-400">Sin datos (analiza correos con IA).</p> : (
            <ResponsiveContainer width="100%" height={Math.max(200, tipos.length * 34)}>
              <BarChart data={tipos.slice(0, 12)} layout="vertical" margin={{ top: 5, right: 15, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="tipo" tick={{ fontSize: 11 }} width={120} />
                <Tooltip />
                <Bar dataKey="total" name="Emails" fill="#6366f1" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent></Card>
      </div>

      {/* Carga por usuario */}
      <Card><CardContent className="p-4">
        <h3 className="text-sm font-semibold mb-2">Carga por usuario (actuaciones confirmadas)</h3>
        {usuarios.length === 0 ? <p className="text-sm text-gray-400">Sin datos.</p> : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={usuarios} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="usuario" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="total_confirmados" name="Confirmados" fill="#3b82f6" radius={[3, 3, 0, 0]} />
              <Bar dataKey="urgentes" name="Urgentes" fill="#ef4444" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent></Card>

      {/* Por remitente (tabla) */}
      <Card><CardContent className="p-4">
        <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-2"><BarChart3 className="h-4 w-4 text-indigo-600" /> Por remitente</h3>
        {remitentes.length === 0 ? <p className="text-sm text-gray-400">Sin datos.</p> : (
          <div className="overflow-x-auto max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white"><tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-3">Remitente</th><th className="py-2 pr-3">Total</th>
                <th className="py-2 pr-3">Confirmados</th><th className="py-2 pr-3">Pendientes</th>
              </tr></thead>
              <tbody>
                {remitentes.map((r, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-2 pr-3 truncate max-w-xs">{r.remitente}</td>
                    <td className="py-2 pr-3">{r.total}</td>
                    <td className="py-2 pr-3 text-green-600">{r.confirmados}</td>
                    <td className="py-2 pr-3 text-amber-600">{r.pendientes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent></Card>
    </div>
  );
};

export default EmailsAbogados;
